import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { ASSETS, SITE, callLink } from "../lib/constants";
import { SERVICES } from "../data/services";
import { useScrollTop } from "../hooks/useReveal";
import AppointmentDialog from "./AppointmentDialog";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", hasMenu: true },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const scrolled = useScrollTop();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();

  // Close on navigation
  const handleNav = () => {
    setOpen(false);
    setServicesOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-[0_1px_24px_-12px_rgba(15,23,42,0.12)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
            onClick={handleNav}
          >
            <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center -my-2 flex-shrink-0">
              <img
                src={ASSETS.logo}
                alt="Lotus Family Dental"
                className="w-full h-full object-contain drop-shadow-sm"
              />
            </div>
           <div className="leading-tight min-w-0">
              <div className="font-heading text-base lg:text-lg font-semibold text-slate-900 tracking-tight font-semibold text-slate-900 tracking-tight truncate">
                Lotus Family Dental
              </div>
              <div className="text-[10px] lg:text-xs  tracking-[0.18em] text-teal-600 font-medium">
                For Every Age, Every Smile.
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) =>
              item.hasMenu ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                        isActive || location.pathname.startsWith("/services")
                          ? "text-teal-700 bg-teal-50"
                          : "text-slate-700 hover:text-teal-700 hover:bg-slate-50"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </NavLink>
                  {servicesOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[640px]">
                      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-3 grid grid-cols-2 gap-1">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            onClick={handleNav}
                            className="px-3 py-2.5 rounded-xl hover:bg-teal-50 transition-colors flex items-start gap-3"
                            data-testid={`menu-service-${s.slug}`}
                          >
                            <div className="w-1 h-1 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-slate-900">{s.name}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{s.tagline}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium rounded-full transition-all ${
                      isActive
                        ? "text-teal-700 bg-teal-50"
                        : "text-slate-700 hover:text-teal-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Right CTAs */}
          <div className="flex items-center gap-2">
            <a
              href={callLink}
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-teal-700 transition-colors px-3 py-2"
              data-testid="header-call-link"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden md:inline">{SITE.phone}</span>
            </a>
            <button
              onClick={() => setDialogOpen(true)}
              className="hidden md:inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-all shadow-sm hover:shadow-lg hover:shadow-teal-600/30"
              data-testid="header-book-button"
            >
              Book Appointment
            </button>

            <button
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-t border-slate-200 max-h-[calc(100vh-4rem)] overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={handleNav}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive ? "bg-teal-50 text-teal-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-2 border-t border-slate-100 mt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 px-4 py-2">
                Services
              </div>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  onClick={handleNav}
                  className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-xl"
                >
                  {s.name}
                </Link>
              ))}
            </div>
            <div className="pt-4 flex flex-col gap-2">
              <a
                href={callLink}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-slate-100 text-slate-900 font-medium"
                data-testid="mobile-call-button"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <button
                onClick={() => {
                  setDialogOpen(true);
                  setOpen(false);
                }}
                className="inline-flex items-center justify-center px-4 py-3 rounded-full bg-teal-600 text-white font-medium"
                data-testid="mobile-book-button"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      <AppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </header>
  );
}
